import { useMemo, useState } from "react";

import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSnackbar } from "notistack";

import { formatNumberFinancial, formatPrice } from "utility/formatters";

import { exportFuturesOptionsWorkbook } from "./exportFuturesOptionsWorkbook";
import FuturesOptionsTable from "./FuturesOptionsTable";
import { FUTURES_OPTIONS_SECTIONS } from "./futuresOptionsView";
import { buildFuturesOptionsView } from "./useFuturesOptionsAdapter";

export { FUTURES_OPTIONS_SECTIONS };

function useFuturesOptionsSectionViews({
  commodityName,
  getTransactionHistoryRowsByCommodity,
  getTransactionHistoryRowsByMatch,
}) {
  const mains = useMemo(
    () => (commodityName ? getTransactionHistoryRowsByCommodity(commodityName) || [] : []),
    [commodityName, getTransactionHistoryRowsByCommodity]
  );

  const sectionViews = useMemo(
    () =>
      FUTURES_OPTIONS_SECTIONS.map((section) => ({
        section,
        ...buildFuturesOptionsView(mains.filter(section.rowFilter), getTransactionHistoryRowsByMatch),
      })),
    [mains, getTransactionHistoryRowsByMatch]
  );

  return { mains, sectionViews, hasAnyTrades: mains.length > 0 };
}

/** P&L key/value table — same accordion/table chrome as Production Summary. */
export function FuturesOptionsPnLCard({
  currentCountyPractice,
  getTransactionHistoryRowsByCommodity,
  getTransactionHistoryRowsByMatch,
}) {
  const commodityName = currentCountyPractice?.commodity_name;
  const wholeDollars = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const { sectionViews } = useFuturesOptionsSectionViews({
    commodityName,
    getTransactionHistoryRowsByCommodity,
    getTransactionHistoryRowsByMatch,
  });
  const rows = sectionViews.map(({ section, totals }) => {
    const isOpen = section.key.startsWith("open-");
    // Open → Open Qty; Closed → Orig. Qty (closed book of record).
    const qty = isOpen ? totals.total_open_qty || 0 : totals.total_orig_qty || 0;
    return {
      key: section.key,
      label: section.title,
      qty,
      qtyFormatted: isOpen ? totals.total_open_qty_formatted : totals.total_orig_qty_formatted,
      gainLoss: totals.total_gain_loss || 0,
      gainLossFormatted: totals.total_gain_loss_formatted,
      testId: `futures-options-summary-${section.key}`,
    };
  });
  const totalQty = rows.reduce((sum, row) => sum + row.qty, 0);
  const totalGainLoss = rows.reduce((sum, row) => sum + row.gainLoss, 0);

  return (
    <TableContainer data-testid="futures-options-pnl-summary">
      <Table stickyHeader aria-label="sticky table" className="reportTable marketing-table">
        <TableHead>
          <TableRow className="tablerow-dark">
            <TableCell>{commodityName || ""}</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right">Gain/Loss</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} hover data-testid={row.testId}>
              <TableCell>{row.label}</TableCell>
              <TableCell align="right">{row.qtyFormatted}</TableCell>
              <TableCell align="right">
                {wholeDollars ? formatPrice(row.gainLoss, 0) : row.gainLossFormatted}
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            hover
            data-testid="futures-options-summary-total"
            className="cash-summary-total tablerow-dark"
          >
            <TableCell>Total</TableCell>
            <TableCell align="right">{formatNumberFinancial(totalQty, 0)}</TableCell>
            <TableCell align="right">{formatPrice(totalGainLoss, wholeDollars ? 0 : 2)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Export control for Positions accordion summary (same Y as title). */
export function FuturesOptionsExportButton({
  currentCountyPractice,
  currentYear,
  getTransactionHistoryRowsByCommodity,
  getTransactionHistoryRowsByMatch,
}) {
  const commodityName = currentCountyPractice?.commodity_name;
  const { enqueueSnackbar } = useSnackbar();
  const [isExporting, setIsExporting] = useState(false);
  const mains = commodityName ? getTransactionHistoryRowsByCommodity(commodityName) || [] : [];
  const hasAnyTrades = mains.length > 0;

  const handleExcelExport = async (event) => {
    event?.stopPropagation?.();
    setIsExporting(true);
    try {
      await exportFuturesOptionsWorkbook({
        getTransactionHistoryRowsByCommodity,
        getTransactionHistoryRowsByMatch,
        commodityName,
        year: currentYear,
      });
    } catch (err) {
      enqueueSnackbar(err?.detail || err?.message || "Export failed.", {
        variant: "error",
        preventDuplicate: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      size="small"
      onClick={handleExcelExport}
      onFocus={(e) => e.stopPropagation()}
      disabled={isExporting || !hasAnyTrades}
      data-testid="futures-options-excel-export"
      startIcon={isExporting ? <CircularProgress size={18} /> : <TableChartOutlinedIcon />}
      className="no-print"
    >
      {isExporting ? "Exporting…" : "Export"}
    </Button>
  );
}

/** Positions section tables (empty sections omitted). */
export default function FuturesOptionsPanels(props) {
  const {
    currentCountyPractice,
    getTransactionHistoryRowsByCommodity,
    getTransactionHistoryRowsByMatch,
  } = props;
  const commodityName = currentCountyPractice?.commodity_name;

  const { sectionViews, hasAnyTrades } = useFuturesOptionsSectionViews({
    commodityName,
    getTransactionHistoryRowsByCommodity,
    getTransactionHistoryRowsByMatch,
  });
  const visibleSections = sectionViews.filter((view) => view.data.length > 0);

  return (
    <Box data-testid="futures-options-panels">
      {!hasAnyTrades ? (
        <Typography variant="body2" color="text.secondary" data-testid="futures-options-empty">
          No futures or options for this commodity.
        </Typography>
      ) : (
        visibleSections.map(({ section, data, totals }, index) => (
          <Box
            key={section.key}
            sx={{ mt: index === 0 ? 0 : 3 }}
            data-testid={`futures-options-section-${section.key}`}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {section.title}
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <FuturesOptionsTable
                {...props}
                data={data}
                totals={totals}
                sectionKey={section.key}
                totalLabel={section.totalLabel}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}
